import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { falTimes } from 'shared/@generated/icons/falTimes';
import type { Breed } from 'shared/@generated/models';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';

export type SelectedBreedsProps = {
  values: (number | string)[];
  breeds: Breed[];
  onRemove?: (value: number | string) => void;
  dense?: boolean;
  testID?: string;
  itemTestID?: (value: number | string) => string;
};

export function SelectedBreeds({
  values,
  breeds,
  onRemove,
  dense = false,
  testID,
  itemTestID,
}: SelectedBreedsProps) {
  const { t } = useTranslation('components');
  const spacing = dense ? 'gap-1 px-1 md:gap-1.5 md:px-1.5' : 'gap-1.5 px-2';

  return (
    <ScrollView
      horizontal={true}
      className={'h-full min-w-0 flex-1'}
      contentContainerClassName={clsx('h-full flex-row items-center', spacing)}
      showsHorizontalScrollIndicator={false}
      keyboardShouldPersistTaps={'handled'}
      testID={testID}
    >
      {values.map((value) => {
        const breed =
          typeof value === 'number' ? breeds.find((breed) => breed.id === value) : undefined;

        return (
          <SelectedBreed
            key={`${typeof value}-${value}`}
            text={
              breed?.name ??
              (typeof value === 'string' ? value : t('listings_filter_form.selected_breed'))
            }
            image={breed?.avatar}
            dense={dense}
            onRemove={onRemove ? () => onRemove(value) : undefined}
            testID={itemTestID?.(value)}
          />
        );
      })}
    </ScrollView>
  );
}

export function SelectedSingleBreed({
  breed,
  text,
  dense = false,
  testID,
}: {
  breed?: Breed;
  text: string;
  dense?: boolean;
  testID?: string;
}) {
  return (
    <View
      accessible={false}
      className={clsx('h-full min-w-0 flex-1 flex-row items-center gap-2', dense ? 'p-1.5' : 'p-2')}
      testID={testID}
    >
      {breed && (
        <Image
          src={breed.avatar}
          alt={breed.name}
          containerClassName={'aspect-square h-full shrink-0'}
          imageClassName={'rounded object-cover'}
          sizeType={'avatar'}
          width={40}
          sizes={'40px'}
        />
      )}
      <Text
        className={clsx('text-brand-brown-dark min-w-0 flex-1', dense ? 'text-base' : 'text-lg')}
        style={
          Platform.OS !== 'web'
            ? { fontSize: dense ? 15 : 18, lineHeight: dense ? 20 : 22 }
            : undefined
        }
        numberOfLines={1}
        ellipsizeMode={'tail'}
      >
        {text}
      </Text>
    </View>
  );
}

export function SelectedBreed({
  text,
  image,
  onRemove,
  dense = false,
  testID,
}: {
  text: string;
  image?: Breed['avatar'];
  onRemove?: () => void;
  dense?: boolean;
  testID?: string;
}) {
  const { t: tCommon } = useTranslation('common');
  const className = clsx(
    'border-brand-brown-dark/15 shadow-brand-brown/5 max-w-60 shrink-0 flex-row items-center rounded-md border bg-white pr-1.5 shadow',
    dense ? 'p-0.5' : 'p-1',
    !image && (dense ? 'pl-2' : 'pl-2.5'),
    dense ? 'h-7 md:h-8' : 'h-10',
  );
  const content = (
    <>
      {image && (
        <Image
          src={image}
          alt={text}
          containerClassName={'aspect-square h-full shrink-0'}
          imageClassName={'rounded-md object-cover'}
          sizeType={'avatar'}
          width={32}
          sizes={'32px'}
        />
      )}
      <Text
        className={clsx(
          'text-brand-brown-dark min-w-0 max-w-40 shrink text-sm',
          image && (dense ? 'ml-1.5' : 'ml-2'),
        )}
        numberOfLines={1}
        ellipsizeMode={'tail'}
      >
        {text}
      </Text>
    </>
  );

  return (
    <View className={className} testID={testID}>
      <View className={'h-full min-w-0 shrink flex-row items-center'}>{content}</View>
      {onRemove && (
        <Pressable
          className={
            'hover:border-brand-brown-dark/30 ml-1 size-5 items-center justify-center rounded-full border border-transparent'
          }
          hitSlop={6}
          accessibilityRole={'button'}
          accessibilityLabel={`${tCommon('delete')} ${text}`}
          testID={testID ? `${testID}-remove` : undefined}
          onPress={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <Icon icon={falTimes} className={'text-brand-brown-dark size-3'} />
        </Pressable>
      )}
    </View>
  );
}
